import React from 'react';
import { motion } from 'framer-motion';
import { plans, mascot } from '../../data/mock';
import { Banana, Check, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { STORE_LINKS } from '../../constants/links';

export const PricingDetails = () => {
  const { t } = useTranslation();
  
  const displayPlans = [
    {
      id: "free",
      name: t('pricing.plans.free.name'),
      subtitle: t('pricing.plans.free.subtitle'),
      bananaHighlight: "15 🍌",
      features: t('pricing.plans.free.features', { returnObjects: true }) || [],
      cta: t('pricing.plans.free.cta'),
      highlighted: false
    },
    {
      id: "backpacker",
      name: t('pricing.plans.backpacker.name'),
      subtitle: t('pricing.plans.backpacker.subtitle'),
      badge: t('pricing.plans.backpacker.badge'),
      bananaHighlight: "200 🍌",
      features: t('pricing.plans.backpacker.features', { returnObjects: true }) || [],
      cta: t('pricing.plans.backpacker.cta'),
      highlighted: true
    },
    {
      id: "business",
      name: t('pricing.plans.business.name'),
      subtitle: t('pricing.plans.business.subtitle'),
      badge: t('pricing.plans.business.badge'),
      bananaHighlight: "450 🍌",
      features: t('pricing.plans.business.features', { returnObjects: true }) || [],
      cta: t('pricing.plans.business.cta'),
      highlighted: false
    }
  ];
  return (
    <section className="py-24 bg-slate-50" id="pricing">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-4">
            {t('pricing.title1')} {t('pricing.title2')}
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            {t('pricing.description')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl w-full mx-auto">
          {displayPlans.map((plan, index) => (
            <motion.div 
              key={plan.id} 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className={`relative bg-white rounded-3xl p-8 border-2 flex flex-col cursor-default ${
                plan.highlighted 
                  ? 'border-[#0DA2E7] shadow-2xl shadow-blue-500/20' 
                  : 'border-slate-100 shadow-xl shadow-slate-200/50'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#0DA2E7] text-white px-4 py-1 rounded-full text-sm font-semibold tracking-wider z-10">
                  {plan.badge}
                </div>
              )}
              
              <div className="flex justify-center mb-6 h-28 items-end">
                <motion.img 
                  animate={{ y: [0, -6, 0] }}
                  transition={{ repeat: Infinity, duration: 3, ease: "easeInOut", delay: index * 0.2 }}
                  src={
                    plan.id === 'free' ? mascot.basic : 
                    plan.id === 'backpacker' ? mascot.traveler : 
                    mascot.pro
                  }
                  alt={`Monito ${plan.name}`}
                  className={`drop-shadow-xl ${plan.id === 'free' ? 'w-24 mb-1' : 'w-28'}`}
                />
              </div>

              <div className="flex justify-between items-center mb-2">
                <h3 className="text-2xl font-semibold text-slate-900">{plan.name}</h3>
                <div className="flex items-center gap-1 bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-semibold">
                  <Banana className="w-4 h-4" /> {plan.bananaHighlight.replace(' 🍌', '')}
                </div>
              </div>
              <div className="mb-6 min-h-[44px]">
                <p className="text-sm font-semibold text-slate-500 leading-snug">{plan.subtitle}</p>
              </div>
              <ul className="space-y-4 mb-8 flex-grow">
                {plan.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className={`mt-1 rounded-full p-0.5 flex-shrink-0 ${plan.highlighted ? 'bg-blue-100 text-[#0DA2E7]' : 'bg-slate-100 text-slate-600'}`}>
                      <Check className="w-3 h-3 stroke-[3]" />
                    </div>
                    <span className="text-slate-600 font-medium text-sm">{feat}</span>
                  </li>
                ))}
              </ul>
              <a 
                href="#download"
                className={`w-full py-4 rounded-xl font-semibold text-lg text-center block transition-all ${
                  plan.highlighted 
                    ? 'bg-[#0DA2E7] text-white hover:bg-blue-600 shadow-lg shadow-blue-500/30' 
                    : 'bg-slate-900 text-white hover:bg-slate-800 hover:shadow-xl'
                }`}
              >
                {plan.cta}
              </a>
            </motion.div>
          ))}
        </div>

        {/* Banana Snacks Section */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="mt-16 bg-white rounded-3xl p-8 border border-slate-100 shadow-lg flex flex-col md:flex-row items-center gap-8 max-w-4xl mx-auto hover:shadow-xl transition-shadow cursor-default"
        >
          <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
            <motion.span 
              initial={{ rotate: -15 }}
              whileInView={{ rotate: [15, -10, 10, 0] }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl"
            >
              🍌
            </motion.span>
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-slate-900 mb-2">{t('pricing.snackTitle')}</h3>
            <p className="text-slate-600 mb-4">
              {t('pricing.snackDesc')}
            </p>
            <div className="flex flex-col gap-2 text-xs text-slate-500">
              <p>• {t('pricing.legal1')}</p>
              <p>• {t('pricing.legal2')}</p>
              <p>• {t('pricing.legal3')}</p>
            </div>
          </div>
        </motion.div>

        {/* Download Buttons */}
        <div className="mt-16 flex flex-wrap justify-center gap-4">
          <a 
            href={STORE_LINKS.ios}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-black text-white px-8 py-4 rounded-2xl hover:scale-105 transition-transform"
          >
            <Download className="w-6 h-6" />
            <div className="text-left">
              <div className="text-xs text-slate-300">{t('finalCTA.downloadLabel')}</div>
              <div className="text-lg font-semibold leading-none">App Store</div>
            </div>
          </a>
          <a 
            href={STORE_LINKS.android}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-black text-white px-8 py-4 rounded-2xl hover:scale-105 transition-transform"
          >
            <Download className="w-6 h-6" />
            <div className="text-left">
              <div className="text-xs text-slate-300">{t('finalCTA.downloadLabel')}</div>
              <div className="text-lg font-semibold leading-none">Google Play</div>
            </div>
          </a>
        </div>

      </div>
    </section>
  );
};
